/**
 * Mapping between the Medical Records Request form draft and the API record.
 *
 * The form exposes three positional request-attempt slots (Initial / 2nd /
 * 3rd Request); the model stores attempts as an ordered list (oldest first).
 * We map the first three loaded attempts into the slots and preserve each
 * row's id so saves update attempts in place rather than churning them. Any
 * attempts beyond the three slots (the legacy source only ever had three) are
 * preserved verbatim so a save can never silently drop them.
 *
 * Date values move through unchanged as 'YYYY-MM-DD' strings: the API serves
 * DateField values in that shape and <input type="date"> expects it. Empty
 * string is the form's "no date" value; it maps to null on the wire.
 */

export const MAX_ATTEMPT_SLOTS = 3;

// Scalar lifecycle date fields: server field name -> form draft key.
const SCALAR_FIELD_TO_KEY = {
  carrier_received_on: 'recordsReceived',
  carrier_reviewed_on: 'recordsReviewed',
  smartlight_requested_on: 'slRequest',
  smartlight_received_on: 'slReceived',
  smartlight_reviewed_on: 'slReviewed',
};

// Positional attempt slots, oldest first.
export const ATTEMPT_SLOT_KEYS = ['initialRequest', 'secondRequest', 'thirdRequest'];

/** A fully-blank draft (every editable field present and empty). */
export function emptyDraft() {
  const draft = {};
  Object.values(SCALAR_FIELD_TO_KEY).forEach((key) => {
    draft[key] = '';
  });
  ATTEMPT_SLOT_KEYS.forEach((key) => {
    draft[key] = '';
  });
  return draft;
}

/**
 * Build the form state from a loaded record (or null for the empty state).
 * @returns {{draft: object, attemptIds: Array<string|null>, extraAttempts: Array}}
 *   - draft: editable field values keyed by form key
 *   - attemptIds: the id backing each slot (null when the slot has no row yet)
 *   - extraAttempts: loaded attempts beyond the three slots, preserved as-is
 */
export function recordToForm(record) {
  if (!record) {
    return {
      draft: emptyDraft(),
      attemptIds: ATTEMPT_SLOT_KEYS.map(() => null),
      extraAttempts: [],
    };
  }

  const draft = {};
  Object.entries(SCALAR_FIELD_TO_KEY).forEach(([field, key]) => {
    draft[key] = record[field] ?? '';
  });

  const attempts = Array.isArray(record.attempts) ? record.attempts : [];
  const attemptIds = ATTEMPT_SLOT_KEYS.map((key, i) => {
    const attempt = attempts[i];
    draft[key] = attempt?.carrier_requested_on ?? '';
    return attempt?.id ?? null;
  });

  return {
    draft,
    attemptIds,
    extraAttempts: attempts.slice(MAX_ATTEMPT_SLOTS),
  };
}

/**
 * Build the POST/PUT payload from the form draft.
 *
 * Scalar dates: empty string -> null. Attempts: each non-empty slot becomes an
 * attempt row carrying its preserved id (so PUT updates it in place); empty
 * slots are omitted so reconciliation deletes any row they previously backed.
 * Extra attempts beyond the slots are appended unchanged so they survive.
 *
 * @param {object} draft
 * @param {Array<string|null>} [attemptIds]
 * @param {Array} [extraAttempts]
 */
export function formToPayload(draft, attemptIds = [], extraAttempts = []) {
  const payload = {};
  Object.entries(SCALAR_FIELD_TO_KEY).forEach(([field, key]) => {
    payload[field] = draft[key] ? draft[key] : null;
  });

  const attempts = [];
  ATTEMPT_SLOT_KEYS.forEach((key, i) => {
    const date = draft[key];
    if (!date) return; // empty slot: omit so reconciliation drops any prior row
    const id = attemptIds[i];
    attempts.push(
      id ? { id, carrier_requested_on: date } : { carrier_requested_on: date }
    );
  });
  extraAttempts.forEach((attempt) => {
    if (attempt?.id && attempt?.carrier_requested_on) {
      attempts.push({
        id: attempt.id,
        carrier_requested_on: attempt.carrier_requested_on,
      });
    }
  });
  payload.attempts = attempts;

  return payload;
}
